// Sector brief — the short spoken overview a SHAPE sector deity gives when the
// user taps it on the home compass: who it is, the sector's Endeavour/Goal,
// its TKNE projects with progress, and its tagged tasks. The string is read
// aloud (Fish TTS) and shown in the chat box, so it is plain first-person prose:
// no markdown, 2-5 short sentences, under ~600 characters, never harsh.
//
// Task membership mirrors KYRS: tasks.sector = letter, OR the task's list has
// task_lists.olmps_sector = letter. Deleted tasks are hard-deleted from `tasks`
// (a snapshot goes to the `trash` table), so there is no deleted flag to filter.
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { SECTORS, type Sector } from '../domain/olmps';
import type { OracleProject } from '../olmps/useOracleProjects';
import { effectiveAims, type OracleSpine } from '../olmps/oracle-spine-data';
import { goalTextOf } from '../olmps/atlas-data';
import { useDataVersion } from '../live/screenContext';
import { SECTOR_NAME } from './theme';

export interface SectorTask {
  id: string;
  title: string;
  done: boolean;
}
export type SectorTasks = Record<Sector, SectorTask[]>;

const OPEN_CAP = 500;
const DONE_CAP = 300;
const DONE_WINDOW_DAYS = 14;
const MAX_CHARS = 600;

function emptyTasks(): SectorTasks {
  return { S: [], H: [], A: [], P: [], E: [] };
}

interface RawRow {
  id: string;
  title: string | null;
  done: boolean | null;
  sector: string | null;
  list_id: string | null;
}

function isSector(v: string | null | undefined): v is Sector {
  return !!v && (SECTORS as string[]).includes(v);
}

/** Loads the user's tasks tagged with each sector: tasks.sector = letter, OR the task's list has task_lists.olmps_sector = letter. Reloads when `rev` changes. */
export function useSectorTasks(userId: string | undefined, rev?: number): SectorTasks {
  const [tasks, setTasks] = useState<SectorTasks>(emptyTasks);
  const dataVersion = useDataVersion();

  useEffect(() => {
    if (!userId) {
      setTasks(emptyTasks());
      return;
    }
    let cancelled = false;
    void (async () => {
      const listsRes = await supabase
        .from('task_lists')
        .select('id, olmps_sector')
        .eq('user_id', userId);
      if (cancelled || listsRes.error) return;

      const listSector = new Map<string, Sector>();
      for (const l of (listsRes.data ?? []) as { id: string; olmps_sector: string | null }[]) {
        if (isSector(l.olmps_sector)) listSector.set(l.id, l.olmps_sector);
      }
      // Narrow server-side to tasks that could belong to a sector. List ids are
      // uuids, so they are safe inside the PostgREST or() filter string.
      const sectorFilter = `sector.in.(${SECTORS.join(',')})`;
      const orFilter =
        listSector.size > 0 ? `${sectorFilter},list_id.in.(${[...listSector.keys()].join(',')})` : sectorFilter;
      const since = new Date(Date.now() - DONE_WINDOW_DAYS * 86400000).toISOString();

      const [openRes, doneRes] = await Promise.all([
        supabase
          .from('tasks')
          .select('id, title, done, sector, list_id')
          .eq('user_id', userId)
          .eq('done', false)
          .or(orFilter)
          .order('created_at', { ascending: false })
          .limit(OPEN_CAP),
        supabase
          .from('tasks')
          .select('id, title, done, sector, list_id')
          .eq('user_id', userId)
          .eq('done', true)
          .gte('updated_at', since)
          .or(orFilter)
          .order('updated_at', { ascending: false })
          .limit(DONE_CAP),
      ]);
      if (cancelled || openRes.error || doneRes.error) return;

      const next = emptyTasks();
      // Fetched newest-first so the cap keeps the freshest rows; iterate oldest
      // first so "first up" is the longest-waiting open task.
      const rows = [
        ...((openRes.data ?? []) as RawRow[]).slice().reverse(),
        ...((doneRes.data ?? []) as RawRow[]),
      ];
      for (const row of rows) {
        const sector = isSector(row.sector) ? row.sector : row.list_id ? listSector.get(row.list_id) : undefined;
        if (!sector) continue;
        next[sector].push({ id: row.id, title: (row.title ?? '').trim() || 'Untitled task', done: !!row.done });
      }
      setTasks(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, rev, dataVersion]);

  return tasks;
}

// ---------------------------------------------------------------------------

function clip(text: string, max: number): string {
  const t = text.replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

interface ProjectLine {
  name: string;
  total: number;
  complete: number;
  next: string | null;
  lapsed: boolean;
}

function projectLine(p: OracleProject): ProjectLine {
  const total = p.steps.length;
  const complete = p.steps.filter((s) => s.status === 'C').length;
  const next = p.steps.find((s) => s.status !== 'C' && s.text.trim())?.text ?? null;
  const worst = p.drift.reduce((m, d) => Math.max(m, d.score), 0);
  return { name: p.name, total, complete, next, lapsed: worst >= 3 };
}

function describeProject(l: ProjectLine): string {
  const name = clip(l.name, 40);
  if (l.total === 0) return `${name} is ready for its first steps`;
  if (l.complete >= l.total) return `${name} is complete, all ${l.total} ${plural(l.total, 'step', 'steps')} done`;
  const head = `${name} is ${l.complete} of ${l.total} ${plural(l.total, 'step', 'steps')} in`;
  return l.next ? `${head}; next up: ${clip(l.next, 50)}` : head;
}

function projectsSentence(projects: OracleProject[], named: number): string {
  const lines = projects.map(projectLine);
  const shown = lines.slice(0, named);
  const more = lines.length - shown.length;
  let s = shown.map(describeProject).join('. ');
  if (more > 0) s += `. And ${more} more ${plural(more, 'project', 'projects')}`;
  return `${s}.`;
}

function closingNudge(args: {
  sectorName: string;
  hasGoal: boolean;
  lapsed: ProjectLine | null;
  open: SectorTask[];
  projects: ProjectLine[];
}): string {
  const { sectorName, hasGoal, lapsed, open, projects } = args;
  if (lapsed) return `${clip(lapsed.name, 40)} could use a little attention when you have a moment.`;
  if (!hasGoal) return `Open ATLAS when you like and name what you want from ${sectorName}.`;
  if (open.length > 0) return 'One small step today keeps it moving.';
  if (projects.some((p) => p.total > 0 && p.complete < p.total)) return 'One small step today keeps it moving.';
  return 'Pick one small thing to add, and we will start there.';
}

/** The spoken overview for one sector. */
export function buildSectorBrief(args: {
  sector: Sector;
  deityName: string;
  node: OracleSpine[Sector] | null | undefined;
  projects: OracleProject[];
  tasks: SectorTask[];
  /** Today's plan items tied to this sector (agenda, scheduled tasks, events). */
  today?: { title: string; start: Date; completed: boolean }[];
}): string {
  const { sector, deityName, node, projects, tasks } = args;
  const today = (args.today ?? []).slice().sort((a, b) => a.start.getTime() - b.start.getTime());
  const sectorName = SECTOR_NAME[sector];
  void deityName; // the deity never introduces itself (Joel): the readout opens on where the sector stands

  const endeavour = (node?.endeavour ?? '').trim();
  const goal = node ? goalTextOf(node).trim() : '';
  const aimCount = node ? effectiveAims(node).length : 0;
  const open = tasks.filter((t) => !t.done);
  const doneCount = tasks.length - open.length;

  // Completely empty sector: say so warmly and suggest one first move.
  if (!endeavour && !goal && aimCount === 0 && projects.length === 0 && tasks.length === 0 && today.length === 0) {
    return `${sectorName} is a blank page today: no Endeavour, no projects, no tasks. First move: name one Endeavour for ${sectorName} in ATLAS, or add a single small task.`;
  }

  const lines = projects.map(projectLine);
  const lapsed = lines.find((l) => l.lapsed) ?? null;

  const build = (named: number, withNudge: boolean): string => {
    // Status first, like a staff-meeting readout.
    const stepsDone = projects.reduce((n, p) => n + p.steps.filter((x) => x.status === 'C').length, 0);
    const stepsAll = projects.reduce((n, p) => n + p.steps.length, 0);
    const status: string[] = [];
    if (projects.length) status.push(`${stepsDone} of ${stepsAll} project steps done across ${projects.length} ${plural(projects.length, 'project', 'projects')}`);
    status.push(`${open.length} ${plural(open.length, 'task', 'tasks')} open`);
    if (doneCount) status.push(`${doneCount} finished recently`);
    const parts: string[] = [`${sectorName} today: ${status.join(', ')}.`];

    if (endeavour) {
      parts.push(`The Endeavour is still ${clip(endeavour, 80)}.`);
    } else if (goal) {
      parts.push(`The goal is ${clip(goal, 80)}.`);
    } else {
      parts.push(`There's no Endeavour set for ${sectorName} yet.`);
    }

    if (today.length) {
      const doneToday = today.filter((x) => x.completed);
      const next = today.filter((x) => !x.completed);
      const at = (d: Date) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      // No time set (a date-only entry sits at local midnight) → say no time, never "at 0:00".
      const when = (x: { start: Date; end?: Date }) => {
        const midnight = x.start.getHours() === 0 && x.start.getMinutes() === 0;
        const span = x.end ? x.end.getTime() - x.start.getTime() : 0;
        return midnight && (span <= 0 || span >= 24 * 3600 * 1000) ? '' : ` at ${at(x.start)}`;
      };
      const bits: string[] = [];
      if (doneToday.length) bits.push(`already done: ${doneToday.slice(0, 2).map((x) => clip(x.title, 40)).join(' and ')}`);
      if (next.length) bits.push(`still ahead: ${next.slice(0, 2).map((x) => `${clip(x.title, 40)}${when(x)}`).join(', then ')}`);
      parts.push(`On today's plan for ${sectorName}, ${bits.join('; ')}.`);
    }

    if (projects.length > 0 && named > 0) parts.push(projectsSentence(projects, named));

    if (open.length > 0) {
      const done = doneCount > 0 ? `, ${doneCount} done` : '';
      void done;
      parts.push(`First up on the task list: ${clip(open[0].title, 50)}.`);
    } else if (doneCount > 0) {
      parts.push(
        `All ${doneCount} of your recent ${sectorName} ${plural(doneCount, 'task is', 'tasks are')} done. Nicely finished.`
      );
    } else if (projects.length === 0) {
      parts.push(`No ${sectorName} tasks yet.`);
    }

    if (withNudge) {
      parts.push(closingNudge({ sectorName, hasGoal: !!(endeavour || goal), lapsed, open, projects: lines }));
    }
    return parts.join(' ');
  };

  // Keep it speakable: shed detail until it fits.
  for (const [named, nudge] of [[2, true], [2, false], [1, false], [0, false]] as const) {
    const text = build(named, nudge);
    if (text.length <= MAX_CHARS) return text;
  }
  return build(0, false).slice(0, MAX_CHARS);
}
